import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SalesCapabilityPanel = ({ data, onChange }) => {
  const maturityLevels = [
    { value: 'initial', label: 'Initial - Ad-hoc processes' },
    { value: 'developing', label: 'Developing - Basic structure' },
    { value: 'defined', label: 'Defined - Documented processes' },
    { value: 'managed', label: 'Managed - Measured & controlled' },
    { value: 'optimized', label: 'Optimized - Continuous improvement' }
  ];

  const capabilityRatings = [
    { value: '1', label: '1 - Basic' },
    { value: '2', label: '2 - Limited' },
    { value: '3', label: '3 - Moderate' },
    { value: '4', label: '4 - Strong' },
    { value: '5', label: '5 - Excellent' }
  ];

  const handleFieldChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-sm">
      <div className="p-16 md:p-20 lg:p-24 border-b border-border">
        <div className="flex items-center gap-12">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-success/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-success" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground font-heading">
              Sales Capability Evaluation
            </h2>
            <p className="text-sm text-muted-foreground caption mt-2">
              Assess sales team structure and process maturity
            </p>
          </div>
        </div>
      </div>
      <div className="p-16 md:p-20 lg:p-24 space-y-20 md:space-y-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20">
          <Input
            label="Dedicated Sales Team Size"
            type="number"
            placeholder="Enter number of sales personnel"
            value={data?.salesTeamSize}
            onChange={(e) => handleFieldChange('salesTeamSize', e?.target?.value)}
            description="Full-time sales staff dedicated to this partnership"
            required
          />

          <Select
            label="Proposal Capability Rating"
            options={capabilityRatings}
            value={data?.proposalCapability}
            onChange={(value) => handleFieldChange('proposalCapability', value)}
            placeholder="Rate proposal development capability"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20">
          <Select
            label="Deal Registration Process Maturity"
            options={maturityLevels}
            value={data?.dealRegistrationMaturity}
            onChange={(value) => handleFieldChange('dealRegistrationMaturity', value)}
            placeholder="Select process maturity level"
            description="Current state of deal registration workflow"
            required
          />

          <Select
            label="Pre-Sales Support Level"
            options={capabilityRatings}
            value={data?.preSalesSupport}
            onChange={(value) => handleFieldChange('preSalesSupport', value)}
            placeholder="Rate pre-sales support capability"
            required
          />
        </div>

        <div className="space-y-12">
          <label className="text-sm font-medium text-foreground">
            Sales Enablement Tools
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
            <Checkbox
              label="CRM System Integration"
              checked={data?.hasCRM}
              onChange={(e) => handleFieldChange('hasCRM', e?.target?.checked)}
            />
            <Checkbox
              label="Sales Automation Tools"
              checked={data?.hasSalesAutomation}
              onChange={(e) => handleFieldChange('hasSalesAutomation', e?.target?.checked)}
            />
            <Checkbox
              label="Proposal Management System"
              checked={data?.hasProposalSystem}
              onChange={(e) => handleFieldChange('hasProposalSystem', e?.target?.checked)}
            />
            <Checkbox
              label="Sales Analytics Dashboard"
              checked={data?.hasSalesAnalytics}
              onChange={(e) => handleFieldChange('hasSalesAnalytics', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-16 md:p-20">
          <h3 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
            <Icon name="Target" size={18} className="text-primary" />
            Sales Performance Indicators
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16">
            <div className="bg-card rounded-lg p-12 md:p-16 border border-border">
              <div className="text-xs text-muted-foreground caption mb-4">Average Deal Size</div>
              <div className="text-xl md:text-2xl font-semibold text-foreground data-text">
                ${data?.avgDealSize || '0'}
              </div>
            </div>
            <div className="bg-card rounded-lg p-12 md:p-16 border border-border">
              <div className="text-xs text-muted-foreground caption mb-4">Win Rate</div>
              <div className="text-xl md:text-2xl font-semibold text-success data-text">
                {data?.winRate || '0'}%
              </div>
            </div>
            <div className="bg-card rounded-lg p-12 md:p-16 border border-border">
              <div className="text-xs text-muted-foreground caption mb-4">Sales Cycle (Days)</div>
              <div className="text-xl md:text-2xl font-semibold text-foreground data-text">
                {data?.salesCycleDays || '0'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesCapabilityPanel;